import React, { useCallback, useState } from "react";
import { getSessionUser } from "../../utils/auth/sessionUser";
import { useLocation, useNavigate } from "react-router-dom";
import { Avatar, Dropdown, Tooltip } from "antd";
import type { MenuProps } from "antd";
import { DownOutlined, HomeOutlined, LeftOutlined, LockOutlined, LogoutOutlined, MenuOutlined, RightOutlined, UserOutlined } from "@ant-design/icons";

import BrandLogo from "../branding/BrandLogo";
import PoweredBy from "../branding/PoweredBy";
import NavItem from "../layout/NavItem";
import PasswordResetModal from "../layout/PasswordResetModal";
import { useBranding } from "../../context/BrandingContext";
import type { User } from "../../types/admin/UserTypes";
import { isCustomerDirectoryMember } from "../../utils/customer/customerDirectory";
import { isPatientSession } from "../../utils/userPrefs/validatePermissions";
import { CUSTOMER_DESTINATIONS, CUSTOMER_PROFILE_PATH } from "./customerDestinations";

interface CustomerSidebarProps {
	collapsed: boolean;
	onCollapse: (v: boolean) => void;
}

const PATIENT_DASHBOARD_PATH = "/patient";

const isActive = (path: string, pathname: string): boolean => pathname === path || pathname.startsWith(`${path}/`);

const readCookieUser = (): Partial<User> => {
	try {
		return getSessionUser();
	} catch {
		return {};
	}
};

// CU-14ygtkj8nr3: the customer rail, built from the same primitives as the staff/patient Sidebar with an explicit allow-list.
const CustomerSidebar: React.FC<CustomerSidebarProps> = ({ collapsed, onCollapse }) => {
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const { branding, isResolved } = useBranding();
	const [mobileOpen, setMobileOpen] = useState(false);
	const [showPasswordReset, setShowPasswordReset] = useState(false);
	// CU-86d45b372: read per render, like Sidebar, so a self-profile save inside the shell updates the footer name.
	const user = readCookieUser();
	const displayName = [user.first_name, user.last_name].filter(Boolean).join(" ");
	const userInitials = `${user.first_name?.[0] ?? ""}${user.last_name?.[0] ?? ""}`.toUpperCase();
	// The switch follows the /patient route guard (session audience); membership is the /customer guard's predicate, never customer_advanced.
	const showPatientDashboardSwitch = isPatientSession() && isCustomerDirectoryMember(user);

	const go = useCallback(
		(path: string) => {
			navigate(path);
			setMobileOpen(false);
		},
		[navigate],
	);

	const profileItems: MenuProps["items"] = [
		{ key: "profile", icon: <UserOutlined />, label: "Profile", onClick: () => go(CUSTOMER_PROFILE_PATH) },
		{ key: "pw", icon: <LockOutlined />, label: "Reset Password", onClick: () => setShowPasswordReset(true) },
		{ type: "divider" },
		{ key: "logout", icon: <LogoutOutlined />, label: "Logout", danger: true, onClick: () => navigate("/logout") },
	];

	const sidebarClass = ["mg-sidebar", collapsed ? "collapsed" : "", mobileOpen ? "mobile-open" : ""].filter(Boolean).join(" ");

	return (
		<>
			{mobileOpen && <div style={{ position: "fixed", inset: 0, zIndex: 99, background: "rgba(0,0,0,0.5)" }} onClick={() => setMobileOpen(false)} />}

			<button
				className="mg-mobile-menu-btn"
				onClick={() => setMobileOpen((v) => !v)}
				aria-label={mobileOpen ? "Close navigation" : "Open navigation"}
			>
				<MenuOutlined />
			</button>

			<aside className={sidebarClass} data-testid="customer-sidebar">
				<div className="mg-sidebar-logo">
					{isResolved && !branding.whitelabel ? (
						<span className="mg-sidebar-logo-text">Xoe</span>
					) : (
						<BrandLogo height={26} className="mg-sidebar-brand-logo" />
					)}
					<button
						className="mg-sidebar-collapse-btn"
						onClick={() => onCollapse(!collapsed)}
						title={collapsed ? "Expand" : "Collapse"}
						data-testid="customer-sidebar-collapse"
					>
						{collapsed ? <RightOutlined /> : <LeftOutlined />}
					</button>
				</div>

				<div className="mg-sidebar-scroll" data-testid="customer-sidebar-scroll">
					<nav aria-label="Customer navigation">
						{!collapsed && <div className="mg-nav-section-heading">Shop</div>}
						{CUSTOMER_DESTINATIONS.map((destination) => (
							<NavItem
								key={destination.key}
								navKey={destination.path}
								label={destination.label}
								icon={<destination.icon />}
								isActive={isActive(destination.path, pathname)}
								collapsed={collapsed}
								testId={`customer-nav-${destination.key}`}
								onClick={() => go(destination.path)}
							/>
						))}
					</nav>

					{showPatientDashboardSwitch && (
						<nav aria-label="Switch dashboard">
							<div className="mg-nav-divider" />
							<NavItem
								navKey={PATIENT_DASHBOARD_PATH}
								label="Patient Dashboard"
								icon={<HomeOutlined />}
								isActive={false}
								collapsed={collapsed}
								testId="customer-nav-patient-dashboard"
								onClick={() => go(PATIENT_DASHBOARD_PATH)}
							/>
						</nav>
					)}
				</div>

				<div className="mg-sidebar-footer">
					<PoweredBy className="mg-sidebar-powered-by" />

					<Dropdown menu={{ items: profileItems }} trigger={["click"]} placement="topRight">
						<Tooltip title={collapsed ? displayName : ""} placement="right" trigger={["hover", "focus"]}>
							<button type="button" className="mg-sidebar-user" aria-haspopup="menu" data-testid="customer-user-menu">
								<Avatar src={user.profile_picture || undefined} size={32} style={{ flexShrink: 0, background: "var(--mg-primary)", fontSize: 12, fontWeight: 600 }}>
									{!user.profile_picture && userInitials}
								</Avatar>
								<div className="mg-sidebar-user-info">
									<div className="mg-sidebar-user-name">{displayName}</div>
								</div>
								{!collapsed && <DownOutlined className="mg-sidebar-user-chevron" />}
							</button>
						</Tooltip>
					</Dropdown>
				</div>
			</aside>

			<PasswordResetModal open={showPasswordReset} onClose={() => setShowPasswordReset(false)} />
		</>
	);
};

export default CustomerSidebar;
